import styled from '@emotion/styled';
import { Text } from 'components/core/text/Text';
import { mediaDesktop, mediaMobile } from 'components/core/theme/breakpoints';
import { useChat } from 'contexts/ChatContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { FeatureFlags, useFlag } from 'hooks/useFlag';
import { FC } from 'react';
import {
  Calendar,
  Clipboard,
  Home,
  MessageCircle,
  Share2,
} from 'react-feather';
import { Link } from 'react-router-dom';
import { Badge, Dot } from './Styles';

export interface AppNavProps { }

const List = styled.nav`
  height: 56px;
  margin-top: 8px;
  display: flex;
  width: 100%;

  ${mediaMobile} {
    justify-content: space-between;
  }

  ${mediaDesktop} {
    height: 100%;
    flex-direction: column;
    padding-right: 80px;
  }
`;

const Spacer = styled.div`
  ${mediaDesktop} {
    padding-top: 15px;
    padding-left: 6px;
  }
`;

const ListItem = styled(Link, { shouldForwardProp: p => p !== 'isSelected' }) <{
  isSelected: boolean;
}>`
  display: inline-flex;
  position: relative;
  flex: 1 1 0%;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  justify-content: center;

  border-radius: 0;
  vertical-align: middle;

  line-height: 1.75;
  min-width: 20%;

  cursor: pointer;
  user-select: none;

  svg {
    width: 20px;
    height: 20px;
  }

  ${mediaDesktop} {
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: flex-start;
    margin: 4px 0;
    padding: 0 16px;
    border-radius: 10px;
    min-width: 180px;
    justify-content: flex-start;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  ${props => (props.isSelected ? 'color: #226e73;' : '')}

  ${Spacer} {
    ${props => (props.isSelected ? 'color: #226e73;' : '')}
  }
`;

const LinkText = styled(Text)`
  font-family: ${props => props.theme.typography.text6.family};
  font-size: 0.875rem;
  margin-bottom: 4px;
  padding-top: 4px;

  ${mediaDesktop} {
    min-height: 60px;
    font-size: 18px;
    font-weight: 100;
  }
`;

const matchRoute = (route: string) =>
  document.location.pathname.indexOf(route) > -1;

const AppNav: FC<AppNavProps> = () => {
  const { state, isSelf } = useCurrentUser();
  const navigation = state?.account?.navigation;
  const { channels } = useChat();
  const totalUnread = channels?.reduce(
    (prev, next) => prev + next.countUnread(),
    0,
  );

  const displayChat = channels && channels.length > 0;
  const displayPlansIndicator = useFlag(FeatureFlags.TreatmentPlanForCoreService) && navigation?.plans?.plansReadyForReviewCount && navigation?.plans?.plansReadyForReviewCount > 0;

  return (
    <>
      <List>
        <>
          <ListItem to="/home" isSelected={matchRoute('/home')}>
            <Home className="inline" />{' '}
            <LinkText variant="heading5">
              <Spacer>Home</Spacer>
            </LinkText>
          </ListItem>

          <ListItem to="/Appointments" isSelected={matchRoute('/Appointments')}>
            <Calendar className="inline" />
            <LinkText variant="heading5">
              <Spacer>Appointments</Spacer>
            </LinkText>
          </ListItem>

          <ListItem to="/plans" isSelected={matchRoute('/plans')}>
            <Clipboard className="inline" />{' '}
            <LinkText variant="heading5">
              <Spacer>Plans</Spacer>
            </LinkText>
            {displayPlansIndicator ? (
              <Badge
                className="ml-4"
                textColor="white"
                backgroundColor="error"
              >
                {navigation?.plans?.plansReadyForReviewCount}
              </Badge>
            ) : null}
          </ListItem>

          {!isSelf && (
            <ListItem to="/community" isSelected={matchRoute('/community')}>
              <Share2 className="inline" />{' '}
              <LinkText variant="heading5">
                <Spacer>Community</Spacer>
              </LinkText>
            </ListItem>
          )}

          {displayChat && (
            <ListItem to="/messaging" isSelected={matchRoute('/messaging')}>
              {totalUnread ? <Dot className="sm:hidden" /> : null}
              <MessageCircle className="inline" />
              <LinkText variant="heading5">
                <Spacer>Chat</Spacer>
              </LinkText>
              {!totalUnread ? null : (
                <Badge
                  className="ml-4"
                  textColor="white"
                  backgroundColor="error"
                >
                  {totalUnread}
                </Badge>
              )}
            </ListItem>
          )}
        </>
      </List>
    </>
  );
};

export default AppNav;
